import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { CategoryService } from '../services/category.service';
import {
  CreateCategoryRequest,
  QuestionCategory,
  UpdateCategoryRequest,
} from '../types/question-bank.types';

/**
 * Hook for managing question categories using React Query
 */
export const useQuestionCategories = (questionType: 'tutorial' | 'question' = 'question') => {
  const [localLoading, setLocalLoading] = useState(true);
  const [subscriptionLimitReached, setSubscriptionLimitReached] =
    useState(false);
  const categoryService = useMemo(() => new CategoryService(), []);
  const queryClient = useQueryClient();
  const categoriesQueryKey = ['question-categories'];

  // Fetch categories and hierarchy
  const {
    data: hierarchyData = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: categoriesQueryKey,
    queryFn: async () => {
      setLocalLoading(true);
      try {
        const data = await categoryService.getCategoryHierarchy(true, questionType);
        return data;
      } catch (error: any) {
        if (error.code === "SUBSCRIPTION_LIMIT_REACHED") {
          setSubscriptionLimitReached(true);
        }
        throw error;
      } finally {
        setLocalLoading(false);
      }
    },
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
  });

  // Extract categories from hierarchy
  const extractCategories = useCallback(
    (categories: any[]): QuestionCategory[] => {
      let result: QuestionCategory[] = [];
      categories.forEach((category) => {
        // Add the current category
        result.push(category);

        // Process subcategories recursively
        if (category.subcategories && category.subcategories.length > 0) {
          result = [...result, ...extractCategories(category.subcategories)];
        }
      });
      return result;
    },
    []
  );

  // Extract flat categories list from hierarchy
  const categories = useMemo(() => {
    return extractCategories(hierarchyData || []);
  }, [hierarchyData, extractCategories]);

  // Custom refresh function that ensures loading state is visible
  const customRefresh = async () => {
    setLocalLoading(true);
    await refetch();
  };

  // Create category mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateCategoryRequest) =>
      categoryService.createCategory(data),
    onSuccess: () => {
      // Invalidate categories query to trigger refetch
      queryClient.invalidateQueries({ queryKey: categoriesQueryKey });
      // Ensure loading state is visible
      setLocalLoading(true);
    },
  });

  // Update category mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryRequest }) =>
      categoryService.updateCategory(id, data),
    onSuccess: () => {
      // Invalidate categories query to trigger refetch
      queryClient.invalidateQueries({ queryKey: categoriesQueryKey });
      // Ensure loading state is visible
      setLocalLoading(true);
    },
  });

  // Delete category mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoryService.deleteCategory(id),
    onSuccess: () => {
      // Invalidate categories query to trigger refetch
      queryClient.invalidateQueries({ queryKey: categoriesQueryKey });
      // Ensure loading state is visible
      setLocalLoading(true);
    },
  });

  // Create category function
  const createCategory = async (data: CreateCategoryRequest) => {
    setLocalLoading(true);
    return createMutation.mutateAsync(data);
  };

  // Update category function
  const updateCategory = async (id: string, data: UpdateCategoryRequest) => {
    setLocalLoading(true);
    return updateMutation.mutateAsync({ id, data });
  };

  // Delete category function
  const deleteCategory = async (id: string) => {
    setLocalLoading(true);
    await deleteMutation.mutateAsync(id);
    return true;
  };

  return {
    categories,
    hierarchy: hierarchyData,
    loading: localLoading || isLoading,
    error: error as Error | null,
    refresh: customRefresh,
    createCategory,
    updateCategory,
    deleteCategory,
    subscriptionLimitReached,
  };
};
